<template>
  <div>
    <div class="resource">
      <nuxt-link :to="`/resources-center/${resource.categorySlug}/${resource.slug}/`" :data-event="event" />
      <div v-if="resource.pinned && !noPin" class="pin">
        <PinIcon />
      </div>
      <div class="resource-image">
        <img loading="lazy" :src="`${$config.imagesUrl}/resources/thumb@240_${resource.image}`" :alt="resource.imageAlt">
      </div>
      <div class="info">
        <div class="badges">
          <span class="cat">{{ resource.category }}</span>
          <span v-if="resource.technology" class="technology" :class="resource.technologySlug">{{ resource.technology }}</span>
        </div>
        <div class="info-wrapper">
          <component :is="heading" class="category h6">
            {{ resource.name }}
          </component>
          <p class="caption" v-html="resource.description" />
          <ButtonArrow
            no-animation type="nuxt-link" :to="`/resources-center/${resource.categorySlug}/${resource.slug}/`"
            text="Learn More"
            :data-event="event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonArrow from '@itbpbg/lms-components/src/components/ButtonArrow';
import global from '@itbpbg/lms-components/src/mixins/global';
import PinIcon from '@itbpbg/lms-components/src/assets/svg/icon-pin.svg?vue-component';

export default {
  components: { ButtonArrow, PinIcon },
  mixins: [global],
  props: {
    resource: {
      type: Object,
      required: true,
    },
    noPin: Boolean,
    event: { type: String, default: '' },
    heading: { type: String, default: 'h6', required: false },
  },
};
</script>

<style lang="scss">
.resource {
  position: relative;
  height: 100%;

  a:not(.btn-arrow) {
    color: inherit;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 3;
    display: block;
  }

  .btn-arrow {
    z-index: 2;
  }

  .pin {
    position: absolute;
    z-index: 2;
    right: 12px;
    top: 12px;
    background: var(--accent-shade-1);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      transform: rotateY(180deg);

      path[fill] {
        fill: transparent !important;
      }

      path[stroke] {
        stroke: var(--main-light) !important;
      }
    }
  }

  .resource-image {
    overflow: hidden;
    position: relative;
    transition: all var(--duration) ease;
    width: 100%;
    height: 212px;
    border-radius: 10px 10px 0 0;
    background: var(--main-light);
    text-align: center;

    img {
      position: absolute;
      width: 128.88px;
      height: 168.55px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      box-shadow: 3.34751px 5.02126px 10.0425px rgba(0, 0, 0, 0.25);
      border-radius: 3.34751px;
    }
  }

  .info {
    background: var(--main-dark-shade-2);
    box-shadow: 0 3px 8px rgba(0, 12, 31, 0.5);
    border-radius: 0 0 10px 10px;
    padding: 8px 0 var(--spacing-16) 0;
    height: calc(100% - 212px);

    .info-wrapper {
      padding-left: var(--spacing-16);
      padding-right: var(--spacing-16);
    }

    .badges {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      padding-left: var(--spacing-16);
      padding-right: 12px;

      span {
        padding: 3px 10px 1px;
        font-weight: 500;
        font-size: 14px;
        line-height: 18px;
        text-align: center;
        border-radius: 100px;
        background: rgba(255, 255, 255, 0.16);
        color: var(--main-light);
        margin-top: 8px;

        &:not(:last-child) {
          margin-right: 8px;
        }

        &.technology {
          text-transform: capitalize;
        }
      }
    }

    .category {
      color: var(--main-light);
      min-height: 40px;
      margin: var(--spacing-26) 0 0;

      @include bp($bp-mobile) {
        min-height: unset;
      }
    }

    .caption {
      margin: var(--spacing-8) 0 var(--spacing-24);
      line-height: 120%;
      color: var(--main-light-shade-4);
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 4;
      display: block;
      display: -webkit-box;
    }
  }

  &:hover {
    transform: scale(1.01);
    transition: all var(--duration) ease;
  }
}

.technology {
  font-weight: 500;

  &.python {
    background: rgba(237, 200, 67, 0.16) !important;
    color: var(--main-yellow) !important;

    a {
      color: var(--main-yellow) !important;
    }
  }

  &.sql {
    background: rgba(86, 191, 199, 0.16) !important;
    color: var(--accent) !important;

    a {
      color: var(--accent) !important;
    }
  }

  &.tableau {
    background: rgba(237, 128, 67, 0.16) !important;
    color: #ED8043 !important;

    a {
      color: #ED8043 !important;
    }
  }

  &.r {
    background: rgba(67, 33, 164, 0.8) !important;
    color: var(--main-purple) !important;

    a {
      color: var(--main-purple) !important;
    }
  }

  &.powerbi {
    background: rgba(123, 97, 255, 0.3) !important;
    color: var(--main-purple) !important;

    a {
      color: var(--main-purple) !important;
    }
  }

  &.excel {
    background: rgba(0, 177, 39, 0.16) !important;
    color: var(--main-green-shade-1) !important;

    a {
      color: var(--main-green-shade-1) !important;
    }
  }

  &.git {
    background: rgba(41, 117, 122, 0.16) !important;
    color: var(--accent-shade-2) !important;

    a {
      color: var(--accent-shade-2) !important;
    }
  }

  &.jupyter {
    background: rgba(255, 160, 160, 0.16) !important;
    color: rgba(255, 97, 97, 0.8) !important;

    a {
      color: rgba(255, 97, 97, 0.8) !important;
    }
  }

  &.theory {
    background: rgba(171, 175, 181, 0.16) !important;
    color: var(--main-dark-shade-4) !important;

    a {
      color: var(--main-dark-shade-4) !important;
    }
  }
}
</style>
